import styled from 'styled-components';
import Modal from './mobile/Modal';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'next-i18next';
import ImageWrapper from './ImageWrapper';
import { useSelector } from 'react-redux';
import Dropdown from '../features/contact/components/organic-form/components/Dropdown';
import { useRouter } from 'next/router';
import Select from 'react-select';
import { addResaleData } from '../new-lib/apis';
import dynamic from 'next/dynamic';

const SuccessModal = dynamic(
  () => import('../features/contact/components/SuccessModal'),
  { ssr: false }
);
interface ModalProps {
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  selectedPropertyType: string;
  deliveryDates: [options: { displayedValue: any }];
}

const initialFormData = {
  unit_area: '',
  number_of_bedrooms: '',
  number_of_bathrooms: '',
  delivery: null,
  finishing: null
};
const completion_types_list = [
  { name: 'not_finished', id: 1, isSelected: false },
  { name: 'semi_finished', id: 2, isSelected: false },
  { name: 'finished', id: 3, isSelected: false },
  { name: 'furnished', id: 4, isSelected: false },
  { name: 'flexi_finished', id: 4, isSelected: false }
];
const nonResidential = ['retail', 'office', 'clinic'];

const SellPropertyModal = ({
  showModal,
  setShowModal,
  selectedPropertyType,
  deliveryDates
}: ModalProps) => {
  const { t } = useTranslation(['sell-my-property', 'common']);
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const { locale } = useRouter();
  const [formData, setFormData] = useState<any>(initialFormData);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [completionTypes, setCompletionTypes] = useState(completion_types_list);
  const [showError, setShowError] = useState(false);
  const filteredDeliveryDates = deliveryDates.map((date, index) =>
    index == 0
      ? {
          value: t('delivered', { ns: 'common' }),
          label: t('delivered', { ns: 'common' })
        }
      : {
          value: Object.values(date)[1]?.displayedValue,
          label: Object.values(date)[1]?.displayedValue
        }
  );
  const isResidential = !nonResidential.includes(
    selectedPropertyType?.toLowerCase()
  );

  const clearFormState = () => {
    setShowModal(false);
    setFormData(initialFormData);
    setCompletionTypes(completion_types_list);
    setShowError(false);
    sessionStorage.setItem('sell_form_data', 'false');
    sessionStorage.removeItem('lead_id');
  };

  const closeModal = () => {
    clearFormState();
    setShowSuccessModal(true);
    setTimeout(() => setShowSuccessModal(false), 2000);
  };

  const submitForm = () => {
    if (!isResidential) {
      setFormData({
        ...formData,
        number_of_bedrooms: 0,
        number_of_bathrooms: 0
      });
    }
    if (
      !Object.values(formData).includes(null) &&
      !Object.values(formData).includes('')
    ) {
      addResaleData({
        ...formData,
        ['delivery']: formData?.delivery?.label,
        lead_id: sessionStorage.getItem('lead_id') as string
      });
      closeModal();
    } else {
      setShowError(true);
    }
  };

  const handleInputChange = (e: any, inputField: string) => {
    const regex = /^[0-9]*$/;
    if (
      (e.target.value.toString().length <= e.target.maxLength &&
        regex.test(e.target.value)) ||
      e.target.value.toString().length == 0
    ) {
      setFormData({ ...formData, [inputField]: e.target.value });
    }
  };

  const handleSelectOption = (option: any) => {
    let newList = completion_types_list.map(type => {
      if (option.id == type.id) {
        setFormData({ ...formData, ['finishing']: option.name });
        return { ...type, isSelected: !type.isSelected };
      } else {
        return type;
      }
    });
    setCompletionTypes(newList);
  };

  return (
    <>
      <Modal
        closeFn={closeModal}
        isOpen={showModal}
        withCloseBtn={true}
        modalWidth={isMobile ? '95%' : '800px'}
        modalHeight={isMobile ? '95%' : 'auto'}
      >
        <ModalContent>
          <HeaderSection>
            <ImageWrapper
              alt="home-sale"
              src={'/assets/images/common/home-sale.png'}
              height={82}
              width={87}
            />
            <span className="headline">{t('modal_headline')}</span>
            <span className="sub-headline">{t('modal_sub_headline')}</span>
          </HeaderSection>

          <Form>
            <FormRow>
              <InputContainer>
                <span className="label">
                  {t('property_area')} <span className="required">*</span>
                </span>
                <InputField>
                  <input
                    value={formData.unit_area}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    name="property_area"
                    id="property_area"
                    placeholder={`${t('ex')} : 120`}
                    onChange={e => handleInputChange(e, 'unit_area')}
                    required
                    maxLength={3}
                  />
                  <span className="placeholder">
                    {t('m2', { ns: 'common' })}
                  </span>
                </InputField>
              </InputContainer>
            </FormRow>
            <FormRow>
              {isResidential && (
                <>
                  <InputContainer>
                    <span className="label">
                      {t('bedrooms_number')} <span className="required">*</span>
                    </span>
                    <InputField>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="bedrooms_number"
                        id="bedrooms_number"
                        placeholder={`${t('ex')} : 4`}
                        onChange={e =>
                          handleInputChange(e, 'number_of_bedrooms')
                        }
                        maxLength={2}
                        value={formData.number_of_bedrooms}
                        required
                      />
                    </InputField>
                  </InputContainer>
                  <InputContainer>
                    <span className="label">
                      {t('bathrooms_number')}{' '}
                      <span className="required">*</span>
                    </span>
                    <InputField>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        name="bathrooms_number"
                        id="bathrooms_number"
                        placeholder={`${t('ex')} : 2`}
                        onChange={e =>
                          handleInputChange(e, 'number_of_bathrooms')
                        }
                        maxLength={2}
                        value={formData.number_of_bathrooms}
                        required
                      />
                    </InputField>
                  </InputContainer>
                </>
              )}
              <InputContainer>
                <span className="label">
                  {t('delivery_status')} <span className="required">*</span>
                </span>
                <Dropdown className="delivery-dropdown" locale={locale}>
                  <Select
                    options={filteredDeliveryDates}
                    name="delivery_status"
                    placeholder={t('delivery_status')}
                    instanceId="delivery_select"
                    onChange={(e: any) => {
                      setFormData({ ...formData, ['delivery']: e });
                    }}
                    className="select"
                    classNamePrefix="select"
                    value={formData.delivery}
                    required
                  />
                </Dropdown>
              </InputContainer>
            </FormRow>
            <FormRow>
              <InputContainer>
                <span className="label">
                  {t('completion_type')} <span className="required">*</span>
                </span>
                <CompletionOptions>
                  {completionTypes.map(option => (
                    <CompletionOption
                      key={option.id}
                      isActive={option.isSelected}
                      onClick={() => handleSelectOption(option)}
                    >
                      {t(option.name, { ns: 'common' })}
                    </CompletionOption>
                  ))}
                </CompletionOptions>
              </InputContainer>
            </FormRow>
            {showError && <span className="error">{t('error_message')}</span>}
          </Form>
          <SubmitBtn formAction="submit" onClick={submitForm}>
            {t('submit')}
          </SubmitBtn>
        </ModalContent>
      </Modal>
      {showSuccessModal && <SuccessModal />}
    </>
  );
};

const ModalContent = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 48px 24px 16px;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  span {
    color: ${props => props.theme.light.colors.black};
    text-align: center;
    &.headline {
      font-size: 21px;
      font-family: materialBold;
    }
    &.sub-headline {
      font-size: 17px;
      font-family: materialLight;
      color: #697586;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0;

  span.error {
    margin-top: 8px;
    text-align: center;
    color: red;
  }
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 16px 0;
  width: 100%;
  :not(:last-child) {
    border-bottom: 1px solid #f0f1f3;
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    flex-direction: column;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;

  span.label {
    font-size: 17px;
    font-family: materialMedium;
    color: ${props => props.theme.light.colors.black};
    line-height: 150%;
  }
  span.required {
    color: red;
  }

  .delivery-dropdown {
    border-radius: ${props => props.theme.borderRadius};
    .select {
      height: 40px;
      .select__control {
        background: #f9fafb !important;
        border: 1px solid #d1d4d9 !important;
      }
      .select__menu {
        min-width: 150px !important;
      }
    }
  }
`;

const InputField = styled.div`
  padding: 8px;
  background-color: #f9fafb;
  border: 1px solid #d1d4d9;
  border-radius: ${props => props.theme.borderRadius};
  height: 44px;
  display: flex;
  align-items: center;
  width: 100%;
  input {
    background-color: transparent;
    outline: none;
    border: none;
    font-size: ${props => props.theme.font.sizes.l};
    width: 100%;
    height: 100%;
    ::-webkit-outer-spin-button,
    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    font-family: materialRegular;
  }
  span.placeholder {
    font-size: 14px;
    color: ${props => props.theme.light.colors.black};
  }
`;

const CompletionOptions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
  flex-wrap: wrap;
`;

const CompletionOption = styled.div<any>`
  background-color: ${props =>
    props.isActive ? props.theme.light.colors.primaryBlue : '#f0f1f3'};
  height: 40px;
  padding: 10px 14px;
  border-radius: ${props => props.theme.borderRadius};
  color: ${props =>
    props.isActive
      ? props.theme.light.colors.white
      : props.theme.light.colors.black};
  font-family: materialMedium;
  border: 1px solid transparent;
  cursor: pointer;
  :hover {
    ${props =>
      !props.isActive &&
      `border: 1px solid ${props.theme.light.colors.primaryBlue};
    color: ${props.theme.light.colors.primaryBlue};
    `}
  }
`;

const SubmitBtn = styled.button`
  width: 30%;
  min-width: 200px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius};
  background-color: #015c9a;
  border: none;
  outline: none;
  color: ${props => props.theme.light.colors.white};
  font-size: ${props => props.theme.font.sizes.s};
  font-family: materialBold;
  align-self: center;
`;

export default SellPropertyModal;
